import type { AppLocale } from "@metaadsmanager/tk-master-contracts";
import { createFormatter, createTranslator } from "use-intl/core";
import { createContext, useContext } from "react";

import { messages } from "./messages";
import { defaultAppLocale, getBrowserTimeZone } from "./config";

type TranslationValues = Record<string, Date | number | string>;
type DateOptions = Intl.DateTimeFormatOptions;
type NumberOptions = Intl.NumberFormatOptions;

export type AppI18n = {
  formatDate(value: Date | number, options?: DateOptions): string;
  formatList(values: Iterable<string>): string;
  formatNumber(value: bigint | number, options?: NumberOptions): string;
  locale: AppLocale;
  setLocale(locale: AppLocale): void;
  /** 日期/筛选边界所用时区（浏览器或 UTC）。 */
  timeZone: string;
  t(key: string, values?: TranslationValues): string;
};

export function createAppI18n(
  locale: AppLocale,
  setLocale: (locale: AppLocale) => void,
  timeZone = getBrowserTimeZone(),
): AppI18n {
  const translator = createTranslator({
    locale,
    messages: messages[locale],
    timeZone,
  });
  const formatter = createFormatter({ locale, timeZone });
  return {
    formatDate: (value, options) => formatter.dateTime(value, options as never),
    formatList: (values) => formatter.list(values),
    formatNumber: (value, options) => formatter.number(value, options as never),
    locale,
    setLocale,
    timeZone,
    t: (key, values) => translator(key as never, values as never),
  };
}

const defaultI18n = createAppI18n(defaultAppLocale, () => undefined);
export const I18nContext = createContext<AppI18n>(defaultI18n);

/** 读取 TK-Master 当前语言、翻译器和 Intl formatter（messages 来自 `src/i18n/messages.ts`）。 */
export const useAppI18n = (): AppI18n => useContext(I18nContext);
